/**
 * Organization Context
 *
 * Manages the currently selected organization and provides
 * organization-scoped API calls with X-Organization-Id header.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { apiGet, invalidateOrgIdCache } from "../utils/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Organization {
  readonly id: string;
  readonly name: string;
  readonly kvkNumber: string | null;
  readonly email: string | null;
  readonly phoneNumber: string | null;
  readonly isActive: boolean;
  readonly organizationType: string;
}

interface Membership {
  readonly id: string;
  readonly role: string;
  readonly status: string;
  readonly joinedAt: string;
  readonly organization: Organization;
}

interface OrganizationContextValue {
  readonly memberships: readonly Membership[];
  readonly currentOrganization: Organization | null;
  readonly currentRole: string | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  selectOrganization: (orgId: string) => void;
  clearOrganization: () => void;
  refreshMemberships: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Storage key — kept in sync with the canonical wrapper's `orgIdCache`.
// ---------------------------------------------------------------------------

const STORAGE_KEY = "denhartog-ze-selected-org";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [memberships, setMemberships] = useState<readonly Membership[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshMemberships = useCallback(async () => {
    if (!isAuthenticated) {
      setMemberships([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiGet<readonly Membership[]>("/organizations/my");
      setMemberships(res.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kon organisaties niet laden");
      setMemberships([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshMemberships();
  }, [refreshMemberships]);

  const selectOrganization = useCallback((orgId: string) => {
    setCurrentOrgId(orgId);
    try {
      localStorage.setItem(STORAGE_KEY, orgId);
    } catch {
      // localStorage might be unavailable
    }
    invalidateOrgIdCache();
  }, []);

  const clearOrganization = useCallback(() => {
    setCurrentOrgId(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  // Derive current org from memberships
  const currentMembership = memberships.find((m) => m.organization.id === currentOrgId) ?? null;
  const currentOrganization = currentMembership?.organization ?? null;
  const currentRole = currentMembership?.role ?? null;

  // Auto-select the first membership when none is stored OR when the
  // stored ID is stale (e.g. another user was logged in previously on this
  // browser and left their org id in localStorage). Without this, the
  // dashboard renders blank because every org-scoped query stays disabled.
  useEffect(() => {
    if (memberships.length === 0) return;
    const stillValid = memberships.some(
      (m) => m.organization.id === currentOrgId,
    );
    if (!stillValid) {
      selectOrganization(memberships[0].organization.id);
    }
  }, [memberships, currentOrgId, selectOrganization]);

  const value: OrganizationContextValue = {
    memberships,
    currentOrganization,
    currentRole,
    isLoading,
    error,
    selectOrganization,
    clearOrganization,
    refreshMemberships,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}
